import {useCallback, useEffect, useRef, useState} from 'react';
import {useRegisterPendingItemDeletionCallback} from '../../hooks/useBasketPendingItemDeletions';
import {useDeleteHandler} from '../../hooks/useDeleteHandler';

export const ITEM_DELETION_DELAY_IN_MS = 3000;
export const ITEM_ANIMATION_DELAY = 500;

export const useBasketDeliveryOptionalRemovedState = ({
  itemKey,
  onDelete,
}: {
  itemKey: string;
  onDelete?: VoidFunction;
}) => {
  const deleteHandler = useDeleteHandler();
  const registerPendingItemDeletion = useRegisterPendingItemDeletionCallback();
  const deletionTimeout = useRef<number>(NaN);
  const endPendingItemDeletion = useRef<VoidFunction | undefined>(undefined);
  const [isItemMarkedForDeletion, setIsItemMarkedForDeletion] = useState(false);
  const [isItemDeleted, setIsItemDeleted] = useState(false);

  const onDeleteIconClick = () => {
    endPendingItemDeletion.current = registerPendingItemDeletion();
    setIsItemMarkedForDeletion(true);
  };

  const deleteItemWithDelay = () => {
    deletionTimeout.current = window.setTimeout(() => {
      if (isItemMarkedForDeletion) {
        deleteItem();
      }
    }, ITEM_DELETION_DELAY_IN_MS + ITEM_ANIMATION_DELAY);
  };

  const deleteItem = useCallback(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    deleteHandler(itemKey)
      .catch(() => {})
      .finally(() => endPendingItemDeletion.current?.());
    setIsItemDeleted(true);
    onDelete?.();
  }, [deleteHandler, itemKey, onDelete]);

  const cancelDeletion = () => {
    setIsItemMarkedForDeletion(false);
    endPendingItemDeletion.current?.();
    clearTimeout(deletionTimeout.current);
  };

  useEffect(() => {
    return () => {
      if (isItemMarkedForDeletion) {
        deleteHandler(itemKey);
      }
      endPendingItemDeletion.current?.();
    };
  }, []);

  return {
    isItemMarkedForDeletion,
    isItemDeleted,
    onDeleteIconClick,
    deleteItemWithDelay,
    cancelDeletion,
    deleteItem,
  };
};
